import React from 'react'
import { FormControlLabel, Checkbox, Typography } from '@mui/material'
import tw from 'twin.macro'
import { getCategoryTitle, getPermissionTitle } from '../../utils'
import { colors } from 'src/theme'

const permSlug = (value: string) =>
  value
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'perm'

interface Props {
  category: string
  availablePermissions: string[]
  selectedPermissions: string[]
  togglePermission: (permissions: string[], categoryPermissions?: string[]) => void
  /** When set, checkboxes get stable E2E ids under this prefix. */
  testIdPrefix?: string
}

export const PermissionsSelector = ({
  category,
  availablePermissions,
  selectedPermissions,
  togglePermission,
  testIdPrefix,
}: Props) => {
  const categorySelected = availablePermissions.filter((permission) =>
    selectedPermissions.includes(permission),
  )
  const allSelected = categorySelected.length === availablePermissions.length
  const partiallySelected = categorySelected.length > 0 && !allSelected

  const handleSelectAll = () => {
    togglePermission(availablePermissions, availablePermissions)
  }

  return (
    <div css={tw`py-3.5 border-0 border-t border-solid border-[#EDEDED] flex flex-col`}>
      <FormControlLabel
        control={
          <Checkbox
            checked={allSelected}
            indeterminate={partiallySelected}
            onChange={handleSelectAll}
            inputProps={
              testIdPrefix
                ? ({
                    'data-testid': `${testIdPrefix}-category-${permSlug(category)}-select-all`,
                  } as React.InputHTMLAttributes<HTMLInputElement>)
                : undefined
            }
            sx={{
              '&.Mui-checked': { color: colors.BASICS_BLACK },
              '&.MuiCheckbox-indeterminate': { color: colors.BASICS_BLACK },
            }}
          />
        }
        label={
          <Typography variant="h5" color={colors.BASICS_BLACK} css={tw`ml-[3px]`}>
            {`${getCategoryTitle(category)} (Select all)`}
          </Typography>
        }
      />
      <div css={tw`pl-6 flex flex-col`}>
        {availablePermissions.map((permission) => (
          <FormControlLabel
            key={permission}
            control={
              <Checkbox
                checked={selectedPermissions.includes(permission)}
                onChange={() => togglePermission([permission])}
                inputProps={
                  testIdPrefix
                    ? ({
                        'data-testid': `${testIdPrefix}-perm-${permSlug(permission)}`,
                      } as React.InputHTMLAttributes<HTMLInputElement>)
                    : undefined
                }
                sx={{ '&.Mui-checked': { color: colors.BASICS_BLACK } }}
              />
            }
            label={
              <Typography variant="subtitle1" color={colors.BASICS_BLACK} css={tw`ml-[3px]`}>
                {getPermissionTitle(permission)}
              </Typography>
            }
          />
        ))}
      </div>
    </div>
  )
}
